import { useCallback, useState, useMemo } from "react";
import { ReactFlow, Background, Controls, type Node, type Edge, applyNodeChanges, applyEdgeChanges, addEdge, type NodeChange, type EdgeChange, type Connection, type OnNodesChange, type OnEdgesChange, type OnConnect } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { StartNode, AgentNode, DecisionNode, ActionNode, EndNode } from "./nodes";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Modal, DialogTrigger, Dialog, ModalOverlay } from "@/components/application/modals/modal";
import { Plus, X, CheckCircle, Users01 } from "@untitledui/icons";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { accountService } from "@/data/mock-accounts";
import type { WorkflowNode, WorkflowEdge, NodeType, DecisionCondition, AgentType, ActionType } from "@/types/workflow";
import type { Account } from "@/types/account";
import { useTranslation } from "react-i18next";

interface WorkflowEditorProps {
    initialNodes: WorkflowNode[];
    initialEdges: WorkflowEdge[];
    initialAccountIds?: string[];
    onSave: (nodes: WorkflowNode[], edges: WorkflowEdge[], accountIds?: string[]) => void;
    onCancel: () => void;
}

const nodeTypes = {
    start: StartNode,
    agent: AgentNode,
    decision: DecisionNode,
    action: ActionNode,
    end: EndNode,
};

export const WorkflowEditor = ({ initialNodes, initialEdges, initialAccountIds = [], onSave, onCancel }: WorkflowEditorProps) => {
    const { t } = useTranslation(['pages:workflows-admin', 'common']);

    // Modal states
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isAccountsModalOpen, setIsAccountsModalOpen] = useState(false);

    // Account selection state
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(initialAccountIds);
    const [accountSearchTerm, setAccountSearchTerm] = useState('');

    // Load available accounts (only SHIPPER type)
    const shipperAccounts = useMemo<Account[]>(() => {
        return accountService.getAll().filter((acc) => acc.userType === "SHIPPER");
    }, []);

    // Filter accounts by search term
    const filteredAccounts = useMemo<Account[]>(() => {
        if (!accountSearchTerm) return shipperAccounts;
        const searchLower = accountSearchTerm.toLowerCase();
        return shipperAccounts.filter(
            (acc) =>
                acc.legalName.toLowerCase().includes(searchLower) ||
                acc.email.toLowerCase().includes(searchLower) ||
                acc.taxId.toLowerCase().includes(searchLower)
        );
    }, [shipperAccounts, accountSearchTerm]);

    // Handle account selection toggle
    const handleAccountToggle = useCallback((accountId: string) => {
        setSelectedAccountIds((prev) => {
            if (prev.includes(accountId)) {
                return prev.filter((id) => id !== accountId);
            } else {
                return [...prev, accountId];
            }
        });
    }, []);

    // Handle modal close and save
    const handleAccountsModalClose = useCallback(() => {
        setIsAccountsModalOpen(false);
        setAccountSearchTerm('');
    }, []);

    // Convert to ReactFlow format
    const [nodes, setNodes] = useState<Node[]>(
        initialNodes.map((node) => ({
            id: node.id,
            type: node.type,
            position: node.position,
            data: node.data,
        }))
    );

    const [edges, setEdges] = useState<Edge[]>(
        initialEdges.map((edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            label: edge.label,
        }))
    );

    const [selectedNode, setSelectedNode] = useState<Node | null>(null);

    // Function to update node data
    const updateNodeData = useCallback((nodeId: string, updates: Partial<Node['data']>) => {
        setNodes((nds) =>
            nds.map((node) =>
                node.id === nodeId
                    ? { ...node, data: { ...node.data, ...updates } }
                    : node
            )
        );

        // Update also selectedNode if it's the one being modified
        if (selectedNode?.id === nodeId) {
            setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, ...updates } } : null);
        }
    }, [selectedNode]);

    const onNodesChange: OnNodesChange = useCallback((changes: NodeChange[]) => {
        setNodes((nds) => applyNodeChanges(changes, nds));

        // Check if selected node was deleted
        const deletedNodeId = changes.find((change) => change.type === "remove" && "id" in change)?.id;
        if (deletedNodeId === selectedNode?.id) {
            setSelectedNode(null);
        }

        // Update selected node if it was changed
        const selectedChange = changes.find((change) => {
            if (change.type === "select" && "id" in change) {
                return change.id === selectedNode?.id;
            }
            return false;
        });
        if (selectedChange && "selected" in selectedChange) {
            if (!selectedChange.selected) {
                setSelectedNode(null);
            }
        }
    }, [selectedNode]);

    const onEdgesChange: OnEdgesChange = useCallback((changes: EdgeChange[]) => {
        setEdges((eds) => applyEdgeChanges(changes, eds));
    }, []);

    const onConnect: OnConnect = useCallback((params: Connection) => {
        setEdges((eds) => addEdge(params, eds));
    }, []);

    const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
        setSelectedNode(node);
    }, []);

    const onPaneClick = useCallback(() => {
        setSelectedNode(null);
    }, []);

    const handleAddNode = useCallback((nodeType: NodeType) => {
        const newNodeId = `node-${Date.now()}`;
        const defaultLabels: Record<NodeType, string> = {
            start: t('workflows.editor.defaultLabels.start'),
            agent: t('workflows.editor.defaultLabels.agent'),
            decision: t('workflows.editor.defaultLabels.decision'),
            action: t('workflows.editor.defaultLabels.action'),
            end: t('workflows.editor.defaultLabels.end'),
        };
        const newNode: Node = {
            id: newNodeId,
            type: nodeType,
            position: { x: Math.random() * 400 + 100, y: Math.random() * 400 + 100 },
            data: {
                label: defaultLabels[nodeType],
            },
        };
        setNodes((nds) => [...nds, newNode]);
    }, [t]);

    const handleSave = useCallback(() => {
        setIsSaving(true);

        const workflowNodes: WorkflowNode[] = nodes.map((node) => ({
            id: node.id,
            type: (node.type || "start") as NodeType,
            position: node.position,
            data: {
                label: (node.data?.label as string) || "",
                ...(node.data || {}),
            },
        }));

        const workflowEdges: WorkflowEdge[] = edges.map((edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || undefined,
            targetHandle: edge.targetHandle || undefined,
            label: (typeof edge.label === "string" ? edge.label : undefined) || undefined,
        }));

        // Call onSave with account_ids
        onSave(workflowNodes, workflowEdges, selectedAccountIds.length > 0 ? selectedAccountIds : undefined);

        // Show loading for 1 second, then success modal
        setTimeout(() => {
            setIsSaving(false);
            setShowSuccessModal(true);

            // After 2 seconds, navigate to list
            setTimeout(() => {
                onCancel();
            }, 2000);
        }, 1000);
    }, [nodes, edges, selectedAccountIds, onSave, onCancel]);

    const handleDeleteNode = useCallback(() => {
        if (selectedNode) {
            setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
            setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
            setSelectedNode(null);
        }
    }, [selectedNode]);

    const nodeTypeOptions = [
        { id: "start", label: t('workflows.editor.nodeTypes.start') },
        { id: "agent", label: t('workflows.editor.nodeTypes.agent') },
        { id: "decision", label: t('workflows.editor.nodeTypes.decision') },
        { id: "action", label: t('workflows.editor.nodeTypes.action') },
        { id: "end", label: t('workflows.editor.nodeTypes.end') },
    ];

    return (
        <div className="flex flex-col h-full">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-secondary bg-primary">
                <div className="flex items-center gap-2">
                    <Dropdown.Root>
                        <Button size="sm" color="secondary" iconLeading={Plus}>
                            {t('workflows.editor.addNode')}
                        </Button>
                        <Dropdown.Popover>
                            <Dropdown.Menu>
                                {nodeTypeOptions.map((option) => (
                                    <Dropdown.Item
                                        key={option.id}
                                        id={option.id}
                                        label={option.label}
                                        onAction={() => handleAddNode(option.id as NodeType)}
                                    />
                                ))}
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown.Root>
                    <Button
                        size="sm"
                        color="secondary"
                        iconLeading={Users01}
                        onClick={() => setIsAccountsModalOpen(true)}
                    >
                        {t('workflows.editor.relatedAccounts')} ({selectedAccountIds.length})
                    </Button>
                    {selectedNode && (
                        <Button size="sm" color="secondary-destructive" onClick={handleDeleteNode}>
                            {t('workflows.editor.deleteNode')}
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" color="primary" onClick={handleSave}>
                        {t('workflows.editor.save')}
                    </Button>
                </div>
            </div>

            {/* Editor Area */}
            <div className="flex flex-1 overflow-hidden">
                {/* React Flow Canvas */}
                <div className="flex-1 relative">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        onPaneClick={onPaneClick}
                        nodeTypes={nodeTypes}
                        nodesDraggable={true}
                        nodesConnectable={true}
                        elementsSelectable={true}
                        panOnDrag={true}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        fitView
                    >
                        <Background />
                        <Controls />
                    </ReactFlow>
                </div>

                {/* Sidebar Properties */}
                <div className="w-80 border-l border-secondary bg-primary flex flex-col">
                    {/* Node Properties - Only when node is selected */}
                    {selectedNode && (() => {
                        const condition = selectedNode.type === "decision" && selectedNode.data?.condition
                            ? (selectedNode.data.condition as DecisionCondition)
                            : null;

                        return (
                            <div className="flex-1 overflow-y-auto p-4">
                                <h3 className="text-lg font-semibold text-primary mb-4">{t('workflows.editor.nodeProperties')}</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.nodeType')}</label>
                                        <div className="text-sm text-tertiary">{selectedNode.type}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.nodeLabel')}</label>
                                        <Input
                                            value={(selectedNode.data?.label as string) || ''}
                                            onChange={(value) => updateNodeData(selectedNode.id, { label: value })}
                                            placeholder={t('workflows.editor.nodeLabel')}
                                        />
                                    </div>
                                    {selectedNode.type === "agent" && (
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.agentType')}</label>
                                            <Select
                                                selectedKey={selectedNode.data?.agent_type as string | undefined}
                                                onSelectionChange={(key) => updateNodeData(selectedNode.id, { agent_type: key as AgentType })}
                                                items={[
                                                    { id: 'postino', label: t('workflows.editor.agentTypes.postino') },
                                                    { id: 'classificatore', label: t('workflows.editor.agentTypes.classificatore') },
                                                    { id: 'parser', label: t('workflows.editor.agentTypes.parser') },
                                                    { id: 'validatore', label: t('workflows.editor.agentTypes.validatore') },
                                                ]}
                                            >
                                                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                            </Select>
                                        </div>
                                    )}
                                    {selectedNode.type === "action" && (
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.actionType')}</label>
                                            <Select
                                                selectedKey={selectedNode.data?.action_type as string | undefined}
                                                onSelectionChange={(key) => updateNodeData(selectedNode.id, { action_type: key as ActionType })}
                                                items={[
                                                    { id: 'notification_in_app', label: t('workflows.editor.actionTypes.notification_in_app') },
                                                    { id: 'notification_email', label: t('workflows.editor.actionTypes.notification_email') },
                                                    { id: 'notification_both', label: t('workflows.editor.actionTypes.notification_both') },
                                                ]}
                                            >
                                                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                            </Select>
                                        </div>
                                    )}
                                    {selectedNode.type === "decision" && (
                                        <div className="space-y-3">
                                            <label className="text-sm font-medium text-primary mb-1 block">{t('workflows.editor.condition')}</label>
                                            <div>
                                                <label className="text-xs font-medium text-secondary mb-1 block">{t('workflows.editor.condition')} - Field</label>
                                                <Input
                                                    value={condition?.field || ''}
                                                    onChange={(value) => {
                                                        const currentCondition = condition || { field: '', operator: 'equals' as const, value: '' };
                                                        updateNodeData(selectedNode.id, {
                                                            condition: {
                                                                ...currentCondition,
                                                                field: value,
                                                            },
                                                        });
                                                    }}
                                                    placeholder="Field name"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-secondary mb-1 block">{t('workflows.editor.condition')} - Operator</label>
                                                <Select
                                                    selectedKey={(condition?.operator || 'equals') as string}
                                                    onSelectionChange={(key) => {
                                                        const currentCondition = condition || { field: '', operator: 'equals' as const, value: '' };
                                                        updateNodeData(selectedNode.id, {
                                                            condition: {
                                                                ...currentCondition,
                                                                operator: key as DecisionCondition['operator'],
                                                            },
                                                        });
                                                    }}
                                                    items={[
                                                        { id: 'equals', label: t('workflows.editor.operators.equals') },
                                                        { id: 'contains', label: t('workflows.editor.operators.contains') },
                                                        { id: 'exists', label: t('workflows.editor.operators.exists') },
                                                        { id: 'not_equals', label: t('workflows.editor.operators.not_equals') },
                                                        { id: 'not_contains', label: t('workflows.editor.operators.not_contains') },
                                                    ]}
                                                >
                                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                                </Select>
                                            </div>
                                            {condition?.operator !== 'exists' && (
                                                <div>
                                                    <label className="text-xs font-medium text-secondary mb-1 block">{t('workflows.editor.condition')} - Value</label>
                                                    <Input
                                                        value={condition?.value ? String(condition.value) : ''}
                                                        onChange={(value) => {
                                                            const currentCondition = condition || { field: '', operator: 'equals' as const, value: '' };
                                                            updateNodeData(selectedNode.id, {
                                                                condition: {
                                                                    ...currentCondition,
                                                                    value: value,
                                                                },
                                                            });
                                                        }}
                                                        placeholder="Value"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Loading Modal */}
            <DialogTrigger isOpen={isSaving}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center gap-3">
                                    <div className="size-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                                    <p className="text-primary font-medium">{t('workflows.editor.savingWorkflow')}</p>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Success Modal */}
            <DialogTrigger isOpen={showSuccessModal}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="size-5 text-utility-success-600" />
                                    <p className="text-primary font-medium">{t('workflows.editor.workflowSaved')}</p>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Related Accounts Modal */}
            <DialogTrigger isOpen={isAccountsModalOpen} onOpenChange={setIsAccountsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('workflows.editor.relatedAccounts')}</h2>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        iconLeading={X}
                                        onClick={handleAccountsModalClose}
                                    >
                                        {t('workflows.editor.close')}
                                    </Button>
                                </div>

                                {/* Search Input */}
                                <div className="mb-4">
                                    <Input
                                        value={accountSearchTerm}
                                        onChange={setAccountSearchTerm}
                                        placeholder={t('workflows.editor.searchAccounts')}
                                    />
                                </div>

                                {/* Accounts List */}
                                <div className="max-h-[500px] overflow-y-auto space-y-2">
                                    {filteredAccounts.length === 0 ? (
                                        <div className="text-center py-8 text-tertiary">
                                            {t('workflows.editor.noAccountsFound')}
                                        </div>
                                    ) : (
                                        filteredAccounts.map((account) => {
                                            const isSelected = selectedAccountIds.includes(account.id);
                                            return (
                                                <div
                                                    key={account.id}
                                                    onClick={() => handleAccountToggle(account.id)}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSelected
                                                        ? 'bg-primary border-brand ring-1 ring-brand'
                                                        : 'bg-primary border-secondary hover:border-primary'
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleAccountToggle(account.id)}
                                                        className="size-4 rounded border-secondary text-brand focus:ring-brand"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-primary truncate">
                                                            {account.legalName}
                                                        </div>
                                                        <div className="text-xs text-tertiary truncate">
                                                            {account.email}
                                                        </div>
                                                        {account.taxId && (
                                                            <div className="text-xs text-tertiary">
                                                                {t('workflows.editor.taxId')}: {account.taxId}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary">
                                    <div className="text-sm text-tertiary">
                                        {t('workflows.editor.selectedCount', { count: selectedAccountIds.length })}
                                    </div>
                                    <Button
                                        size="md"
                                        color="primary"
                                        onClick={handleAccountsModalClose}
                                    >
                                        {t('workflows.editor.save')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
